/**
 * Single-image uploader.
 *
 * Calls api.uploadImage(token, file) and returns the resulting URL via
 * `onChange`. Shows live preview + size/mime indicator + remove button.
 *
 * The parent owns the URL string state (so it composes with RHF easily):
 *   <ImageUploader value={url} onChange={setUrl} aspect="banner" />
 *
 * Phase 4 hardening:
 *  - Pre-flight type/size guard before the network round-trip
 *  - Canvas-based downscale if the source is > MAX_BYTES (no new deps)
 *  - Min-dimension hint (640×360); 16:9 is advisory not enforced
 *  - Surfaces backend's {detail:{code,message}} or Pydantic detail array
 */
import { useRef, useState } from 'react';
import { Image as ImageIcon, Trash2, UploadCloud } from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { classes } from '@/lib/utils';

const ASPECT = {
  square: 'aspect-square',
  banner: 'aspect-[3/1]',
  card:   'aspect-[4/3]',
};

// Must mirror /app/backend/image_storage.py
const ACCEPTED_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const ACCEPTED_HUMAN = 'PNG, JPEG, or WEBP';
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const MIN_W = 640;
const MIN_H = 360;
// Target output for client-side downscale of oversized originals.
const TARGET_W = 1920;
const TARGET_H = 1080;
const JPEG_QUALITY = 0.82;

function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file doesn't look like a valid image."));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode image'))),
      mime,
      quality,
    );
  });
}

// Downscale oversize images client-side so we don't burn a 413 on the
// network. Always re-encodes as JPEG (smallest payload of the accepted
// types, parity with backend acceptance).
async function downscaleIfOversize(file) {
  if (file.size <= MAX_BYTES) return file;
  const img = await fileToImage(file);
  const ratio = Math.min(TARGET_W / img.width, TARGET_H / img.height, 1);
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  const blob = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
  // If even the downscaled output is still over the cap, give up — the
  // user gets a clean error rather than a 413 surprise.
  if (blob.size > MAX_BYTES) {
    throw new Error(
      `Image is too large even after compression (${Math.round(blob.size / 1024 / 1024)} MB). Please pick a smaller image.`,
    );
  }
  // Repackage as File so the multipart form keeps a clean filename.
  const base = (file.name || 'image').replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

async function validateAndPrep(file) {
  // 1) MIME allowlist (also enforced server-side; client check gives an
  //    instant error rather than a network round-trip).
  if (!ACCEPTED_MIMES.includes(file.type)) {
    throw new Error(`Only ${ACCEPTED_HUMAN} images are accepted.`);
  }
  // 2) Hard size cap — anything larger gets downscaled below.
  // 3) Min-dimension hint: load the image so we can check W/H. We do this
  //    BEFORE downscale so the user gets the most helpful error.
  let img;
  try {
    img = await fileToImage(file);
  } catch (e) {
    throw new Error("That file doesn't look like a valid image.");
  }
  if (img.width < MIN_W || img.height < MIN_H) {
    throw new Error(
      `Image is too small (${img.width}×${img.height}). Please use at least ${MIN_W}×${MIN_H}.`,
    );
  }
  // 4) 16:9 hint — advisory only. Tracked so the parent can show it
  //    inline; not raised as an error.
  const ratio = img.width / img.height;
  const aspectHint =
    Math.abs(ratio - 16 / 9) > 0.05
      ? `Tip: 16:9 ratio renders best (yours is ${img.width}:${img.height}).`
      : null;
  // 5) Downscale if oversize, otherwise pass through.
  const finalFile = await downscaleIfOversize(file);
  return { finalFile, aspectHint };
}

export default function ImageUploader({
  value, onChange, aspect = 'square', label = 'Upload image', testID,
}) {
  const token = useAuthStore((s) => s.token);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [hint, setHint] = useState('');

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr(''); setHint('');
    try {
      const { finalFile, aspectHint } = await validateAndPrep(f);
      const res = await api.uploadImage(token, finalFile);
      onChange?.(res.url);
      if (aspectHint) setHint(aspectHint);
    } catch (ex) {
      // ex.detail can be:
      //   - a string formatted by formatError() (our preferred shape)
      //   - an object {code, message} from {detail:{code,message}} backend errors
      //   - undefined for client-side validation Errors (ex.message)
      const detailMsg =
        (ex?.detail && typeof ex.detail === 'object' && ex.detail.message) ||
        (typeof ex?.detail === 'string' ? ex.detail : null);
      setErr(detailMsg || ex?.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <div data-testid={testID}>
      <div
        className={classes(
          'relative w-full overflow-hidden rounded-2xl border border-dashed border-white/15 bg-black/30',
          'flex items-center justify-center group',
          ASPECT[aspect] || ASPECT.square,
        )}
      >
        {value ? (
          <img
            src={resolveImageUrl(value)}
            alt="preview"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center text-white/40">
            <ImageIcon size={28} />
            <span className="text-xs mt-2">{label}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={busy}
            data-testid="img-upload-trigger"
            className="px-3 py-1.5 text-xs rounded-lg bg-gold text-black font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {busy ? (
              <span className="w-3 h-3 border-2 border-black/40 border-t-black rounded-full animate-spin" />
            ) : (
              <UploadCloud size={14} />
            )}
            {value ? 'Replace' : 'Upload'}
          </button>
          {value ? (
            <button
              type="button"
              onClick={() => onChange?.('')}
              className="px-3 py-1.5 text-xs rounded-lg bg-white/10 text-white inline-flex items-center gap-1.5 hover:bg-white/20"
            >
              <Trash2 size={14} /> Clear
            </button>
          ) : null}
        </div>
      </div>
      <input
        ref={ref}
        type="file"
        accept={ACCEPTED_MIMES.join(',')}
        className="hidden"
        onChange={onFile}
      />
      {err ? <div className="mt-2 text-xs text-red-400" data-testid="img-upload-error">{err}</div> : null}
      {!err && hint ? <div className="mt-2 text-xs text-white/40">{hint}</div> : null}
    </div>
  );
}
